import { apiSecond } from '@/boot/axios'

import { aquaApiPrefix } from './aquaForms'

const SUBJECT_FORMS = [1, 2, 4, 5, 6]
const FEED_FORMS = [3]
const CONTACT_KEYS = ['address', 'phone', 'email', 'executor_name', 'head_full_name']
const ZPM_FISH_FORMS = [1, 2]
const STABLE_ROW_KEYS = [
	'fishery_object_name',
	'water_object_name',
	'farm_establishment_year',
	'stocking_material_source',
	'water_supply_source',
	'lake_farm_area_ha',
	'cage_farm_volume_m3',
	'uzv_pool_volume_m3',
	'pond_area_ha',
	'domestic_feed_supplier_name',
	'imported_feed_country',
	'domestic_feed_used_name',
	'imported_feed_used_name',
]

// Каждый источник необязателен: недоступный сервис не должен мешать заполнять отчёт вручную
const safe = (promise) => promise.then((response) => response.data).catch(() => null)

async function latestReport(forms) {
	const lists = await Promise.all(forms.map((item) => safe(apiSecond.get(aquaApiPrefix('subject', item)))))
	const reports = lists.flatMap((list) => list?.data || [])
	if (!reports.length) return null
	const isNewer = (item, best) => item.report_year > best.report_year || (item.report_year === best.report_year && item.id > best.id)
	const latest = reports.reduce((best, item) => (isNewer(item, best) ? item : best))
	const details = await safe(apiSecond.get(`${aquaApiPrefix('subject', latest.form)}/${latest.id}`))
	return details?.data || null
}

async function activeZpm(regionId) {
	const response = await safe(apiSecond.get('v1/zpm/user-data'))
	const records = (response?.data?.data || []).filter((item) => item.status === 'active' && !item.archived_at)
	return records.find((item) => item.region_id === regionId) || records[0] || null
}

export async function loadObjectOptions() {
	const response = await safe(apiSecond.get('/v2/handbook-plots', { params: { page: 1, per_page: 1000 } }))
	const titles = (response?.data || []).map((plot) => [plot.reservoir?.title, plot.title].filter(Boolean).join(' — '))
	return [...new Set(titles)]
}

function stableValues(row) {
	if (!row) return {}
	return Object.fromEntries(STABLE_ROW_KEYS.filter((key) => row[key] !== null && row[key] !== undefined && row[key] !== '').map((key) => [key, row[key]]))
}

function buildRows(form, zpm, previousRows, objectOptions) {
	const defaultObject = !FEED_FORMS.includes(form) && objectOptions.length === 1 ? objectOptions[0] : null
	const objectKey = form === 1 ? 'fishery_object_name' : 'water_object_name'
	const withDefault = (row) => (defaultObject && !row[objectKey] ? { ...row, [objectKey]: defaultObject } : row)

	if (zpm?.fishes?.length) {
		let copied = 0
		const rows = zpm.fishes.map((fish) => {
			const previous = stableValues(previousRows.find((row) => row.fish_id === fish.fish_id))
			if (Object.keys(previous).length) copied++
			const row = { ...previous, fish_id: fish.fish_id }
			if (form === 1) row.production_capacity_ton = fish.design_capacity
			return withDefault(row)
		})
		return { rows, copied }
	}
	const rows = previousRows.map((row) => withDefault({ ...stableValues(row), fish_id: row.fish_id ?? null }))
	return { rows, copied: rows.length }
}

export async function loadAquaPrefill(form, regionId) {
	const forms = form === 3 ? FEED_FORMS : SUBJECT_FORMS
	const [gbd, previous, sameForm, zpm, objectOptions] = await Promise.all([
		safe(apiSecond.get(`${aquaApiPrefix('subject', form)}/gbd-ul`)),
		latestReport(forms),
		latestReport([form]),
		ZPM_FISH_FORMS.includes(form) ? activeZpm(regionId) : null,
		loadObjectOptions(),
	])

	const header = {}
	const sources = []
	const activities = gbd?.status === 'found' ? gbd.organization?.activities || [] : []

	if (activities.length) {
		header.oked_main = activities[0].oked
		if (activities[1]) header.oked_secondary = activities[1].oked
		sources.push({ key: 'gbd', keys: Object.keys(header), params: { name: gbd.organization.name_ru } })
	}

	if (previous) {
		const keys = [...CONTACT_KEYS]
		if (!activities.length) keys.push('oked_main', 'oked_secondary')
		const filled = keys.filter((key) => previous[key])
		filled.forEach((key) => {
			header[key] = previous[key]
		})
		const key = filled.includes('oked_main') ? 'previous_oked' : 'previous'
		if (filled.length) sources.push({ key, keys: filled, params: { form: previous.form, year: previous.report_year } })
	}

	if (form === 1 && sameForm?.bank_account) {
		header.bank_account = sameForm.bank_account
		sources.push({ key: 'bank', keys: ['bank_account'], params: { year: sameForm.report_year } })
	}

	const { rows, copied } = buildRows(form, zpm, sameForm?.rows || [], objectOptions)
	if (zpm?.fishes?.length) sources.push({ key: 'zpm', keys: ['rows'], params: { nikad: zpm.zpm_nikad, count: rows.length } })
	if (copied) sources.push({ key: 'rows', keys: ['rows'], params: { form, year: sameForm.report_year } })

	return { header, rows, sources, objectOptions }
}
